<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useDonorPlan } from '@/composables/useDonorPlan'
import { useUpgradeModal } from '@/composables/useUpgradeModal'
import { useDashboardToast } from '@/composables/useDashboardToast'
import PaidPlanBadge from '@/components/dashboard/ui/PaidPlanBadge.vue'

const { t } = useI18n()
const store = useStore()
const { isPaid, isPlanKnown, renewsAt, endsAt, subscriptionPrice, subscriptionInterval } =
  useDonorPlan()
const { openUpgradeModal } = useUpgradeModal()
const { showToast } = useDashboardToast()

const intervalLabel = computed(() =>
  subscriptionInterval.value === 'month'
    ? t('Dashboard.Account.Plan.PerMonth')
    : t('Dashboard.Account.Plan.PerYear')
)

// The subscription is ending when a lapse date is set and there is no renewal.
const isEnding = computed(() => !renewsAt.value && !!endsAt.value)

const detailLabel = computed(() =>
  isEnding.value
    ? t('Dashboard.Account.Plan.EndsOn')
    : t('Dashboard.Account.Plan.RenewsOn')
)

const detailValue = computed(
  () =>
    (isEnding.value ? endsAt.value : renewsAt.value) ??
    t('Dashboard.Common.NotSet')
)

const onManageSubscription = async () => {
  // Open the tab synchronously so it isn't blocked as a non-user-gesture popup,
  // then point it at the Stripe billing portal once the session URL resolves.
  const portalTab = window.open('about:blank', '_blank')
  try {
    const session = await store.dispatch('fetchBillingPortalSession')
    if (!session?.url) {
      throw new Error('Billing portal session did not return a URL')
    }
    if (portalTab) {
      portalTab.opener = null
      portalTab.location.href = session.url
    } else {
      window.location.assign(session.url)
    }
  } catch {
    portalTab?.close()
    showToast(t('Dashboard.Account.Plan.Toast.ManageError'))
  }
}
</script>

<template>
  <section class="sa-acct-section" aria-labelledby="account-plan-heading">
    <h2 id="account-plan-heading">{{ $t('Dashboard.Account.Plan.Heading') }}</h2>

    <template v-if="isPlanKnown">
    <div v-if="!isPaid" class="sa-plan-free">
      <div class="sa-plan-card sa-plan-card--free">
        <div class="sa-plan-card__main">
          <div class="sa-plan-card__hd">
            <div class="sa-plan-card__brand">
              <span>Swiftaid</span>
              <span class="sa-tag-free">{{ $t('Dashboard.Account.Plan.SummaryFree') }}</span>
            </div>
          </div>

          <p class="sa-plan-free__lead">{{ $t('Dashboard.Account.Plan.FreeLead') }}</p>

          <p id="account-plan-free-includes-label" class="sa-plan-free__includes-label">
            {{ $t('Dashboard.Account.Plan.FreeIncludesLabel') }}
          </p>
          <ul
            class="sa-feature-list sa-plan-free__features"
            aria-labelledby="account-plan-free-includes-label"
          >
            <li v-for="index in 2" :key="index">
              <div class="sa-check-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <span>{{ $t(`Dashboard.Account.Plan.FreeIncludes.${index - 1}`) }}</span>
            </li>
          </ul>
        </div>

        <div
          class="sa-plan-free__upgrade"
          aria-labelledby="account-plan-free-upgrade-heading"
        >
          <h3 id="account-plan-free-upgrade-heading" class="sa-plan-free__upgrade-title">
            {{ $t('Dashboard.Account.Plan.FreeUpgradeHeading') }}
          </h3>
          <p class="sa-plan-free__upgrade-text">
            {{ $t('Dashboard.Account.Plan.FreeUpgradeNote') }}
          </p>
          <button type="button" class="sa-btn-purple sa-btn-purple--block" @click="openUpgradeModal">
            {{ $t('Dashboard.Home.UpgradeCta') }}
            <PaidPlanBadge />
          </button>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="sa-plan-card sa-plan-card--paid">
        <div class="sa-plan-card__main">
          <div class="sa-plan-card__hd">
            <div class="sa-plan-card__brand">
              <span>Swiftaid</span>
              <PaidPlanBadge />
              <span class="sa-tag-active">{{ $t('Dashboard.Account.Plan.Active') }}</span>
            </div>
            <div v-if="subscriptionPrice" class="sa-plan-card__price">
              {{ subscriptionPrice }} <span>{{ intervalLabel }}</span>
            </div>
          </div>
          <div class="sa-detail-row">
            <span class="sa-detail-label">{{ detailLabel }}</span>
            <span class="sa-detail-value">{{ detailValue }}</span>
          </div>
        </div>

        <div class="sa-plan-paid__footer">
          <div class="sa-plan-paid__footer-actions">
            <button
              type="button"
              class="sa-btn-secondary"
              @click="onManageSubscription"
            >
              {{ $t('Dashboard.Account.Plan.ManageSubscription') }}
            </button>
          </div>
        </div>
      </div>
    </template>
    </template>
  </section>
</template>
